/**
 * Restrict the advertising pages to the MONITORED campaign set of the active
 * site (C-fix3) — not the whole account. The watched set is the source of truth;
 * audits are filtered client-side to those campaigns and the KPIs recomputed.
 */
import type { GoogleAdsAudit, GoogleAdsCampaign } from '@silospot/shared';
import { useQuery } from '@tanstack/react-query';
import { api } from './api';

/** Monitored campaigns for an account. `watchedIds`/`watched` are null until loaded. */
export function useWatchedSet(
  projectId: string | null,
  cid: string | null,
): {
  watchedIds: Set<string> | null;
  watched: GoogleAdsCampaign[] | null;
  watchedCount: number;
  loadingWatched: boolean;
} {
  const q = useQuery({
    queryKey: ['gads-watched', projectId, cid],
    queryFn: () => api.googleAdsWatched(projectId as string, cid as string),
    enabled: !!projectId && !!cid,
  });
  return {
    watchedIds: q.data ? new Set(q.data.watched.map((c) => c.id)) : null,
    watched: q.data?.watched ?? null,
    watchedCount: q.data?.watched.length ?? 0,
    loadingWatched: q.isLoading,
  };
}

/** Keep only the watched campaigns and recompute totals. invalidClicks is scaled
 *  by the watched clicks share (no per-campaign breakdown) — an ESTIMATE. */
export function filterAuditToWatched(audit: GoogleAdsAudit, ids: Set<string>): GoogleAdsAudit {
  const campaigns = audit.campaigns.filter((c) => ids.has(c.campaignId));
  const clicks = campaigns.reduce((s, c) => s + c.clicks, 0);
  const conversions = campaigns.reduce((s, c) => s + c.conversions, 0);
  const costEur = campaigns.reduce((s, c) => s + c.costEur, 0);
  const wastedEur = campaigns.reduce((s, c) => s + c.wastedEur, 0);
  const avgWasteScore = campaigns.length
    ? Math.round(campaigns.reduce((s, c) => s + c.wasteScore, 0) / campaigns.length)
    : 0;
  const share = audit.totals.clicks > 0 ? clicks / audit.totals.clicks : 0;
  const invalidClicks = Math.round(audit.totals.invalidClicks * share);
  return {
    ...audit,
    campaigns,
    totals: { costEur, clicks, invalidClicks, conversions, wastedEur, avgWasteScore },
  };
}

/** Short relative time ("il y a 32 min") in the given locale, or "—" when null. */
export function relTime(iso: string | null, lang: string): string {
  if (!iso) return '—';
  const min = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 60000));
  const rtf = new Intl.RelativeTimeFormat(lang, { numeric: 'auto' });
  if (min < 60) return rtf.format(-min, 'minute');
  const h = Math.round(min / 60);
  if (h < 24) return rtf.format(-h, 'hour');
  return rtf.format(-Math.round(h / 24), 'day');
}
